import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import useTheme from '@mui/material/styles/useTheme';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../auth/AuthContext';
import { auth as authApi } from '../../services/api/auth';

/**
 * Standalone local login form at /auth/local-login.
 * Authenticates against Django's own user database — no Keycloak involved.
 * Requires MIMIR_LOCAL_LOGIN_ENABLED = True on the backend.
 */
export default function LocalLoginForm() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const { loginWithTokens } = useAuth();
    const navigate = useNavigate();

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const handleSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            setError(null);
            setLoading(true);
            try {
                const { data } = await authApi.localLogin(username, password);
                loginWithTokens(data);
                navigate('/', { replace: true });
            } catch (err) {
                setError(err?.response?.data?.detail || err?.message || 'Login failed');
            } finally {
                setLoading(false);
            }
        },
        [username, password, loginWithTokens, navigate],
    );

    return (
        <Dialog
            fullScreen={fullScreen}
            open={true}
            PaperProps={{
                sx: {
                    borderRadius: fullScreen ? 0 : 3,
                    minWidth: fullScreen ? 'auto' : 380,
                    maxWidth: 420,
                    overflow: 'hidden',
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: 700,
                    fontSize: '1.3rem',
                    pt: 4,
                    pb: 1,
                    letterSpacing: '-0.02em',
                }}
            >
                Mimir
            </DialogTitle>

            <Box sx={{ textAlign: 'center', pb: 1 }}>
                <Typography variant='body2' color='text.secondary'>
                    {process.env.REACT_APP_ENV || 'development'}
                </Typography>
            </Box>

            <DialogContent sx={{ pt: 0, px: 4, pb: 4 }}>
                <form onSubmit={handleSubmit}>
                    <Box display='flex' flexDirection='column' gap={2.5} sx={{ paddingTop: '12px' }}>
                        <TextField
                            id='username'
                            data-testid='username'
                            label='Username'
                            variant='outlined'
                            size='small'
                            fullWidth
                            required
                            autoFocus
                            value={username}
                            autoComplete='username'
                            onChange={(e) => setUsername(e.target.value)}
                            error={!!error}
                        />

                        <TextField
                            id='password'
                            data-testid='password'
                            label='Password'
                            variant='outlined'
                            size='small'
                            fullWidth
                            required
                            type={showPassword ? 'text' : 'password'}
                            autoComplete='current-password'
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            error={!!error}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position='end'>
                                        <IconButton
                                            aria-label='toggle password visibility'
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge='end'
                                            size='small'
                                        >
                                            {showPassword ? (
                                                <Visibility fontSize='small' />
                                            ) : (
                                                <VisibilityOff fontSize='small' />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {error && (
                            <Typography color='error' variant='body2' textAlign='center'>
                                {error}
                            </Typography>
                        )}

                        <Box display='flex' justifyContent='flex-end' pt={1.5}>
                            <Button
                                type='submit'
                                color='primary'
                                variant='contained'
                                disableElevation
                                disabled={loading}
                                sx={{
                                    textTransform: 'none',
                                    minWidth: 120,
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    py: 1,
                                }}
                            >
                                {loading ? <CircularProgress size={20} /> : 'Sign in'}
                            </Button>
                        </Box>
                    </Box>
                </form>
            </DialogContent>
        </Dialog>
    );
}
